<template>
  <div class="alert-prefs">

    <div class="section-header">
      <span class="material-symbols-outlined">notifications_active</span>
      <h3>Alertes par utilisateur</h3>
    </div>

    <p class="prefs-intro">
      Le seuil global de chaque mesure et le réglage global de chaque utilisateur
      restent en place. Ce qui est défini ici les remplace, pour cet utilisateur
      et cet univers uniquement.
    </p>

    <div v-if="loadError" class="prefs-error">
      <span class="material-symbols-outlined">error_outline</span>
      Préférences non chargées : {{ loadError }}
      <button type="button" class="prefs-retry" @click="load">Réessayer</button>
    </div>

    <div v-if="loading" class="prefs-empty">Chargement…</div>

    <div v-else-if="!users.length" class="prefs-empty">
      Aucun utilisateur n'est rattaché à cet univers. Ajoutez-en depuis l'onglet
      Utilisateurs.
    </div>

    <div v-else class="prefs-layout">

      <!-- Liste des utilisateurs -->
      <aside class="prefs-users">
        <button
            v-for="u in users"
            :key="u.id"
            type="button"
            class="prefs-user"
            :class="{ active: selectedUserId === u.id }"
            @click="selectUser(u.id)"
        >
          <span class="prefs-user-avatar">{{ initials(u) }}</span>
          <span class="prefs-user-body">
            <span class="prefs-user-name">{{ u.firstname }} {{ u.lastname }}</span>
            <span class="prefs-user-mail">{{ u.email }}</span>
          </span>
          <span class="prefs-user-badges">
            <span
                v-if="u.pending"
                class="prefs-dot pending"
                title="Rattachement pas encore enregistré : enregistrez l'univers avant de régler ses préférences"
            >!</span>
            <span
                class="prefs-dot"
                :class="effectiveClass(u, 'sms')"
                :title="'SMS : ' + effectiveLabel(u, 'sms')"
            >SMS</span>
            <span
                v-if="countCustom(u)"
                class="prefs-count"
                :title="countCustom(u) + ' seuil(s) personnalisé(s)'"
            >{{ countCustom(u) }}</span>
          </span>
        </button>
      </aside>

      <!-- Panneau de l'utilisateur sélectionné -->
      <section class="prefs-panel" v-if="selected">

        <div v-if="selected.pending" class="prefs-pending">
          <span class="material-symbols-outlined">info</span>
          Ce rattachement n'est pas encore enregistré. Enregistrez l'univers,
          puis revenez ici pour régler ses préférences.
        </div>

        <!-- Abonnement -->
        <div class="prefs-block">
          <div class="prefs-block-head">
            <span class="material-symbols-outlined">campaign</span>
            <h4>Abonnement à cet univers</h4>
          </div>

          <p class="prefs-block-hint">
            « Hérité » suit le réglage global du compte. Les deux autres choix ne
            valent que pour cet univers.
          </p>

          <div class="prefs-channel">
            <div class="prefs-channel-label">
              <span class="material-symbols-outlined">sms</span>
              SMS
              <em class="prefs-inherit-note">
                global : {{ selected.global_optin_sms ? 'activé' : 'désactivé' }}
              </em>
            </div>
            <div class="prefs-segmented">
              <button
                  v-for="opt in tristate"
                  :key="'sms' + String(opt.value)"
                  type="button"
                  :class="['seg-btn', { active: draft.optin_sms === opt.value }]"
                  :disabled="!editable"
                  @click="draft.optin_sms = opt.value"
              >{{ opt.label }}</button>
            </div>
          </div>

          <div class="prefs-channel">
            <div class="prefs-channel-label">
              <span class="material-symbols-outlined">mail</span>
              Email
              <em class="prefs-inherit-note">
                global : {{ selected.global_optin_mail ? 'activé' : 'désactivé' }}
              </em>
            </div>
            <div class="prefs-segmented">
              <button
                  v-for="opt in tristate"
                  :key="'mail' + String(opt.value)"
                  type="button"
                  :class="['seg-btn', { active: draft.optin_mail === opt.value }]"
                  :disabled="!editable"
                  @click="draft.optin_mail = opt.value"
              >{{ opt.label }}</button>
            </div>
          </div>
        </div>

        <!-- Seuils -->
        <div class="prefs-block">
          <div class="prefs-block-head">
            <span class="material-symbols-outlined">tune</span>
            <h4>Seuils personnels</h4>
            <button
                v-if="editable"
                type="button"
                class="prefs-link"
                @click="resetAll"
            >Tout remettre au global</button>
          </div>

          <p class="prefs-block-hint">
            Laissez min et max vides pour suivre le seuil global de la mesure.
            Dès qu'une valeur est saisie, elle remplace le seuil global pour cet
            utilisateur.
          </p>

          <div v-if="!sensors.length" class="prefs-empty">
            Cet univers n'a aucune mesure.
          </div>

          <table v-else class="prefs-table">
            <thead>
            <tr>
              <th>Mesure</th>
              <th class="num">Seuil global</th>
              <th class="num">Min perso</th>
              <th class="num">Max perso</th>
              <th class="center">Actif</th>
              <th></th>
            </tr>
            </thead>
            <tbody>
            <tr
                v-for="sensor in sensors"
                :key="sensor.id"
                :class="{ customized: isCustom(sensor.id) }"
            >
              <td>
                <span class="prefs-sensor-name">{{ sensor.name }}</span>
                <span class="prefs-sensor-unit" v-if="sensor.unit_of_measure">
                    {{ sensor.unit_of_measure }}
                  </span>
              </td>

              <td class="num muted">
                {{ sensor.max !== null && sensor.max !== undefined ? sensor.max : '—' }}
              </td>

              <td class="num">
                <input
                    type="number"
                    step="any"
                    class="prefs-input"
                    :disabled="!editable"
                    v-model="draft.thresholds[sensor.id].min"
                    placeholder="—"
                />
              </td>

              <td class="num">
                <input
                    type="number"
                    step="any"
                    class="prefs-input"
                    :disabled="!editable"
                    v-model="draft.thresholds[sensor.id].max"
                    placeholder="—"
                />
              </td>

              <td class="center">
                <input
                    type="checkbox"
                    :disabled="!editable"
                    v-model="draft.thresholds[sensor.id].enabled"
                    :title="draft.thresholds[sensor.id].enabled
                      ? 'Alertes actives sur cette mesure'
                      : 'Aucune alerte sur cette mesure pour cet utilisateur'"
                />
              </td>

              <td class="center">
                <button
                    v-if="editable && isCustom(sensor.id)"
                    type="button"
                    class="prefs-reset"
                    title="Revenir au seuil global"
                    @click="resetSensor(sensor.id)"
                >
                  <span class="material-symbols-outlined">undo</span>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <div class="prefs-actions" v-if="editable">
          <span class="prefs-dirty" v-if="dirty">Modifications non enregistrées</span>
          <button
              type="button"
              class="prefs-save"
              :disabled="!dirty || saving || selected.pending"
              @click="save"
          >
            <span class="material-symbols-outlined">save</span>
            {{ saving ? 'Enregistrement…' : 'Enregistrer' }}
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  name: "SiteAlertPreferences",

  props: {
    // L'objet site du formulaire : c'est lui qui fait foi pour la liste des
    // utilisateurs et des mesures, y compris ceux pas encore enregistres.
    site: {type: Object, required: true},
    editable: {type: Boolean, default: true},
  },

  data() {
    return {
      loading: false,
      saving: false,

      // Preferences venant de l'API, indexees par id d'utilisateur.
      prefs: {},
      loadError: null,

      selectedUserId: null,

      draft: {
        optin_sms: null,
        optin_mail: null,
        thresholds: {},
      },

      initialDraft: "",

      tristate: [
        {value: null, label: "Hérité"},
        {value: 1, label: "Oui"},
        {value: 0, label: "Non"},
      ],
    };
  },

  computed: {
    siteId() {
      return this.site ? this.site.id : null;
    },

    /** Les mesures telles que le formulaire les connait. */
    sensors() {
      return this.site.sensors || [];
    },

    /**
     * Les utilisateurs de l'onglet Utilisateurs, enrichis de leurs preferences.
     * Un utilisateur ajoute mais pas encore enregistre apparait ici, marque
     * comme tel : il n'a pas encore de ligne de pivot a modifier.
     */
    users() {
      return (this.site.users || []).map((u) => {
        const pref = this.prefs[u.id] || null;

        return {
          id: u.id,
          firstname: u.firstname,
          lastname: u.lastname,
          email: u.email,
          phone: u.phone,
          global_optin_sms: Number(u.optin_sms || 0),
          global_optin_mail: Number(u.optin_mail === undefined ? 0 : u.optin_mail),
          optin_sms: pref ? pref.optin_sms : null,
          optin_mail: pref ? pref.optin_mail : null,
          thresholds: pref ? pref.thresholds : {},
          // Pas de preference connue cote serveur => rattachement non enregistre.
          pending: !pref,
        };
      });
    },

    selected() {
      return this.users.find((u) => u.id === this.selectedUserId) || null;
    },

    dirty() {
      return JSON.stringify(this.draft) !== this.initialDraft;
    },
  },

  watch: {
    siteId: {
      immediate: true,
      handler(v) {
        if (v) this.load();
      },
    },
  },

  methods: {
    async load() {
      if (!this.siteId) return;

      this.loading = true;
      this.loadError = null;

      try {
        const data = await this.$store.dispatch("alertPreferencesSiteRequest", {
          siteId: this.siteId,
        });

        const prefs = {};

        for (const u of (data.users || [])) {
          prefs[u.id] = {
            optin_sms: u.optin_sms,
            optin_mail: u.optin_mail,
            thresholds: u.thresholds || {},
          };
        }

        this.prefs = prefs;

      } catch (err) {
        // On n'efface plus la liste en silence : l'erreur doit se voir.
        const data = err && err.response && err.response.data;
        this.loadError = (data && data.message) || data || "Préférences illisibles.";
        this.showErr(err);
      } finally {
        this.loading = false;

        if (this.users.length && !this.selected) {
          this.selectUser(this.users[0].id);
        }
      }
    },

    selectUser(userId) {
      if (this.dirty && this.selectedUserId !== null && this.selectedUserId !== userId) {
        if (!window.confirm("Des modifications ne sont pas enregistrées. Changer d'utilisateur ?")) {
          return;
        }
      }

      this.selectedUserId = userId;

      const user = this.users.find((u) => u.id === userId);
      if (!user) return;

      const thresholds = {};

      for (const sensor of this.sensors) {
        const existing = (user.thresholds || {})[sensor.id];

        thresholds[sensor.id] = {
          min: existing && existing.min !== null ? existing.min : "",
          max: existing && existing.max !== null ? existing.max : "",
          enabled: existing ? !!existing.enabled : true,
        };
      }

      this.draft = {
        optin_sms: user.optin_sms === null || user.optin_sms === undefined ? null : Number(user.optin_sms),
        optin_mail: user.optin_mail === null || user.optin_mail === undefined ? null : Number(user.optin_mail),
        thresholds,
      };

      this.initialDraft = JSON.stringify(this.draft);
    },

    isCustom(sensorId) {
      const t = this.draft.thresholds[sensorId];
      if (!t) return false;
      return t.min !== "" || t.max !== "" || !t.enabled;
    },

    countCustom(user) {
      return Object.keys(user.thresholds || {}).length;
    },

    /** Ce que l'utilisateur reçoit réellement sur cet univers. */
    effective(user, channel) {
      const pivot = channel === "sms" ? user.optin_sms : user.optin_mail;

      if (pivot !== null && pivot !== undefined) {
        return Number(pivot) === 1;
      }

      return channel === "sms" ? !!user.global_optin_sms : !!user.global_optin_mail;
    },

    effectiveClass(user, channel) {
      const pivot = channel === "sms" ? user.optin_sms : user.optin_mail;
      const on = this.effective(user, channel);
      const overridden = pivot !== null && pivot !== undefined;

      return {on, off: !on, overridden};
    },

    effectiveLabel(user, channel) {
      const pivot = channel === "sms" ? user.optin_sms : user.optin_mail;
      const on = this.effective(user, channel) ? "activé" : "désactivé";
      const overridden = pivot !== null && pivot !== undefined;

      return on + (overridden ? " (réglé pour cet univers)" : " (hérité du global)");
    },

    initials(user) {
      const f = (user.firstname || "").charAt(0);
      const l = (user.lastname || "").charAt(0);
      return (f + l).toUpperCase() || "?";
    },

    resetSensor(sensorId) {
      this.$set(this.draft.thresholds, sensorId, {min: "", max: "", enabled: true});
    },

    resetAll() {
      for (const sensor of this.sensors) {
        this.resetSensor(sensor.id);
      }
    },

    async save() {
      this.saving = true;

      try {
        await this.$store.dispatch("alertPreferenceSubscriptionRequest", {
          siteId: this.siteId,
          userId: this.selectedUserId,
          optin_sms: this.draft.optin_sms,
          optin_mail: this.draft.optin_mail,
        });

        const thresholds = this.sensors.map((sensor) => {
          const t = this.draft.thresholds[sensor.id];

          return {
            sensor_id: sensor.id,
            min: t.min === "" ? null : Number(t.min),
            max: t.max === "" ? null : Number(t.max),
            enabled: t.enabled ? 1 : 0,
          };
        });

        await this.$store.dispatch("alertPreferenceThresholdsRequest", {
          siteId: this.siteId,
          userId: this.selectedUserId,
          thresholds,
        });

        this.$store.commit("setSnack", {
          type: "green",
          err: "Préférences d'alerte enregistrées.",
        });

        const keepUser = this.selectedUserId;
        this.initialDraft = JSON.stringify(this.draft);
        await this.load();
        this.selectUser(keepUser);
      } catch (err) {
        this.showErr(err);
      } finally {
        this.saving = false;
      }
    },

    showErr(err) {
      const message =
          (err && err.response && err.response.data) || "Une erreur est survenue";

      this.$store.commit("setSnack", {
        type: "red",
        err: typeof message === "string" ? message : JSON.stringify(message),
      });
    },
  },
};
</script>

<style scoped>
.prefs-error {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: #a94442;
  background: #fdf3f2;
  border: 1px solid #f0d6d4;
  border-radius: 8px;
  padding: 9px 13px;
  margin-bottom: 14px;
}

.prefs-error .material-symbols-outlined {
  font-size: 18px;
}

.prefs-retry {
  margin-left: auto;
  background: none;
  border: 1px solid #a94442;
  color: #a94442;
  border-radius: 6px;
  padding: 3px 10px;
  font-size: 0.74rem;
  cursor: pointer;
}

.prefs-pending {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: #8a6d3b;
  background: #fdf8ef;
  border: 1px solid #f0e2c8;
  border-radius: 8px;
  padding: 9px 13px;
  margin-bottom: 14px;
}

.prefs-pending .material-symbols-outlined {
  font-size: 18px;
}

.prefs-dot.pending {
  background: #f0ad4e;
  color: #fff;
}

.alert-prefs {
  width: 100%;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.section-header h3 {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2d24;
  margin: 0;
}

.prefs-intro {
  font-size: 0.82rem;
  color: #6b7a70;
  margin: 0 0 18px;
  max-width: 760px;
}

.prefs-empty {
  font-size: 0.85rem;
  color: #8a9a90;
  font-style: italic;
  padding: 16px 0;
}

.prefs-layout {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 20px;
  align-items: start;
}

@media (max-width: 900px) {
  .prefs-layout {
    grid-template-columns: 1fr;
  }
}

/* --- Liste utilisateurs --- */
.prefs-users {
  border: 1px solid #e6ebe8;
  border-radius: 10px;
  overflow: hidden;
  max-height: 520px;
  overflow-y: auto;
  background: #fff;
}

.prefs-user {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  background: none;
  border: none;
  border-bottom: 1px solid #f0f3f1;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
}

.prefs-user:hover {
  background: #f6f8f7;
}

.prefs-user.active {
  background: #eef5f0;
  box-shadow: inset 3px 0 0 #2d5a3d;
}

.prefs-user-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #2d5a3d;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.prefs-user-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.prefs-user-name {
  font-size: 0.85rem;
  color: #1f2d24;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.prefs-user-mail {
  font-size: 0.72rem;
  color: #8a9a90;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.prefs-user-badges {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.prefs-dot {
  font-size: 0.6rem;
  font-weight: 700;
  padding: 2px 5px;
  border-radius: 4px;
  letter-spacing: 0.04em;
}

.prefs-dot.on {
  background: #dff0e4;
  color: #2d5a3d;
}

.prefs-dot.off {
  background: #f0f0f0;
  color: #a0a0a0;
}

.prefs-dot.overridden {
  outline: 1px solid #2d5a3d;
}

.prefs-count {
  font-size: 0.6rem;
  font-weight: 700;
  background: #2d5a3d;
  color: #fff;
  border-radius: 8px;
  padding: 2px 6px;
}

/* --- Panneau --- */
.prefs-panel {
  min-width: 0;
}

.prefs-block {
  border: 1px solid #e6ebe8;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
}

.prefs-block-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.prefs-block-head h4 {
  font-size: 0.92rem;
  font-weight: 600;
  color: #1f2d24;
  margin: 0;
  flex: 1;
}

.prefs-block-hint {
  font-size: 0.78rem;
  color: #8a9a90;
  margin: 0 0 14px;
}

.prefs-link {
  background: none;
  border: none;
  color: #2d5a3d;
  font-size: 0.76rem;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
}

.prefs-channel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 0;
  border-top: 1px solid #f0f3f1;
  flex-wrap: wrap;
}

.prefs-channel-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: #1f2d24;
}

.prefs-inherit-note {
  font-size: 0.72rem;
  color: #a0aca5;
  font-style: normal;
  margin-left: 4px;
}

.prefs-segmented {
  display: inline-flex;
  border: 1px solid #d8e0db;
  border-radius: 8px;
  overflow: hidden;
}

.seg-btn {
  background: #fff;
  border: none;
  padding: 6px 14px;
  font-size: 0.76rem;
  color: #6b7a70;
  cursor: pointer;
  transition: all 0.15s;
}

.seg-btn + .seg-btn {
  border-left: 1px solid #d8e0db;
}

.seg-btn.active {
  background: #2d5a3d;
  color: #fff;
  font-weight: 600;
}

.seg-btn:disabled {
  cursor: default;
  opacity: 0.6;
}

/* --- Table des seuils --- */
.prefs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}

.prefs-table th {
  text-align: left;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8a9a90;
  font-weight: 600;
  padding: 6px 8px;
  border-bottom: 1px solid #e6ebe8;
}

.prefs-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #f4f6f5;
  color: #1f2d24;
}

.prefs-table tr.customized td {
  background: #f7fbf8;
}

.prefs-table .num {
  text-align: right;
  width: 110px;
}

.prefs-table .center {
  text-align: center;
  width: 60px;
}

.prefs-table .muted {
  color: #a0aca5;
}

.prefs-sensor-name {
  font-weight: 500;
}

.prefs-sensor-unit {
  font-size: 0.72rem;
  color: #a0aca5;
  margin-left: 4px;
}

.prefs-input {
  width: 100%;
  border: 1px solid #d8e0db;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 0.82rem;
  text-align: right;
  color: #1f2d24;
  background: #fff;
}

.prefs-input:focus {
  outline: none;
  border-color: #2d5a3d;
}

.prefs-input:disabled {
  background: #f6f8f7;
  color: #8a9a90;
}

.prefs-reset {
  background: none;
  border: none;
  color: #8a9a90;
  cursor: pointer;
  display: inline-flex;
  padding: 2px;
}

.prefs-reset:hover {
  color: #2d5a3d;
}

.prefs-reset .material-symbols-outlined {
  font-size: 18px;
}

/* --- Actions --- */
.prefs-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.prefs-dirty {
  font-size: 0.76rem;
  color: #c9822a;
}

.prefs-save {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #2d5a3d;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 9px 18px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.15s;
}

.prefs-save:disabled {
  opacity: 0.45;
  cursor: default;
}

.prefs-save .material-symbols-outlined {
  font-size: 18px;
}
</style>
